import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import JSZip from 'jszip';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import { ReviewCaveat, GeneratedMark } from '../components/ReviewAid';
import SubpoenaAnalysis from '../components/analysis/SubpoenaAnalysis';

// ── CSV Parsing ────────────────────────────────────────────────────────────────

function parseCSVLine(line) {
  const result = [];
  let inQuote = false;
  let cur = '';
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuote = !inQuote;
    } else if (ch === ',' && !inQuote) {
      result.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur.trim());
  return result;
}

// Parse a Snapchat CSV file (possibly multi-section).
// Returns { dateRange: string, noData: bool, sections: [{ headers, rows }] }
function parseSnapCSV(text) {
  const lines = text.split('\n');
  let dateRange = '';
  let noData = false;
  const sections = [];
  let state = 'meta'; // 'meta' | 'expectHeaders' | 'data'
  let headers = [];
  let rows = [];

  const flush = () => {
    if (headers.length > 0 && rows.length > 0) sections.push({ headers: [...headers], rows: [...rows] });
    headers = []; rows = [];
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (!trimmed) continue;
    const fields = parseCSVLine(trimmed);
    const first = fields[0] || '';

    if (first.startsWith('Date range searched:')) {
      dateRange = first.replace('Date range searched:', '').trim();
      continue;
    }
    if (first.includes('NO RESPONSIVE DATA FOUND')) { noData = true; continue; }
    if (/^-{5,}$/.test(first)) { if (state === 'data') flush(); state = 'meta'; continue; }
    if (/^={5,}$/.test(first)) { if (state === 'data') flush(); state = 'expectHeaders'; continue; }

    if (state === 'expectHeaders') {
      headers = fields.filter(f => f !== '');
      state = 'data';
    } else if (state === 'data') {
      const row = {};
      headers.forEach((h, i) => { row[h] = fields[i] || ''; });
      rows.push(row);
    }
    // state === 'meta': ignore line
  }
  flush();
  return { dateRange, noData, sections };
}

// Find a field value by scanning all sections for the one whose headers include fieldName
function getField(sections, fieldName) {
  const sec = sections.find(s => s.headers.includes(fieldName));
  return sec?.rows?.[0]?.[fieldName] ?? '';
}

// ── Helper UI Components ───────────────────────────────────────────────────────

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const IP_PAGE_SIZE = 50;

const paginBtnStyle = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  color: '#475569',
};

function Chip({ label, color, bg }) {
  return (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bg, color, letterSpacing: '.03em', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#94a3b8', marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function KVRow({ label, value, chip }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
      <div style={{ width: 160, flexShrink: 0, fontSize: 12, color: '#64748b', fontWeight: 500 }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {value ? <span>{value}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
        {chip}
      </div>
    </div>
  );
}

function DataTable({ headers, rows }) {
  return (
    <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ background: '#f8fafc' }}>
          {headers.map(h => (
            <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9', background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
            {headers.map(h => (
              <td key={h} style={{ padding: '6px 10px', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row[h]}>
                {row[h] || <span style={{ color: '#cbd5e1' }}>—</span>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function SnapchatProcessor() {
  const [phase, setPhase] = useState('upload'); // 'upload' | 'parsing' | 'dashboard'
  const [accounts, setAccounts] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [analysisView, setAnalysisView] = useState(null); // null | 'ips' | 'links'
  const [ipFilter, setIpFilter] = useState('');
  const [ipPage, setIpPage] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const account = accounts[selectedIdx] ?? null;
  const ipRows = useMemo(() => account?.csvParsed?.ip_data?.sections?.[0]?.rows ?? [], [account]);

  const filteredIpRows = useMemo(() => {
    if (!ipFilter.trim()) return ipRows;
    const f = ipFilter.toLowerCase();
    return ipRows.filter(row => Object.values(row).some(v => String(v).toLowerCase().includes(f)));
  }, [ipRows, ipFilter]);

  const totalIpPages = Math.max(1, Math.ceil(filteredIpRows.length / IP_PAGE_SIZE));
  const pagedIpRows = filteredIpRows.slice(ipPage * IP_PAGE_SIZE, (ipPage + 1) * IP_PAGE_SIZE);

  // Reset filter/page when switching accounts
  useEffect(() => { setIpFilter(''); setIpPage(0); }, [selectedIdx]);

  const processFile = useCallback(async (file) => {
    trackToolAction('/snapchat', 'Snapchat Subpoena Processor');
    setPhase('parsing');
    setError('');
    setStatusMsg('Opening archive…');
    try {
      const zip = await JSZip.loadAsync(file);

      // Group entries by account folder (level-1 folders directly inside the ZIP)
      // Expected: Archive.zip / account-folder / file.csv
      const entryMap = {}; // accountFolder → { [csvKey]: ZipEntry }
      zip.forEach((path, entry) => {
        if (entry.dir) return;
        const parts = path.split('/').filter(Boolean);
        if (parts.length < 2) return; // need account-folder/file.csv
        const accountFolder = parts[0];
        const fname = parts[parts.length - 1].toLowerCase();
        const key = ['subscriber_info', 'ip_data', 'public_profile', 'loc_priv_sets'].find(k => fname.startsWith(k));
        if (!key) return;
        if (!entryMap[accountFolder]) entryMap[accountFolder] = {};
        entryMap[accountFolder][key] = entry;
      });

      if (Object.keys(entryMap).length === 0)
        throw new Error('No account folders found. Expected: Archive.zip → account-folder/ → subscriber_info.csv, ip_data.csv, etc.');

      const parsed = [];
      for (const [folder, entries] of Object.entries(entryMap)) {
        setStatusMsg(`Parsing ${folder}…`);
        const csvParsed = {};
        for (const key of ['subscriber_info', 'ip_data', 'public_profile', 'loc_priv_sets']) {
          csvParsed[key] = entries[key]
            ? parseSnapCSV(await entries[key].async('text'))
            : { dateRange: '', noData: true, sections: [] };
        }
        const subSecs = csvParsed.subscriber_info.sections;
        const locSecs = csvParsed.loc_priv_sets.sections;
        parsed.push({
          folderName: folder,
          dateRange: csvParsed.subscriber_info.dateRange,
          info: {
            username:    getField(subSecs, 'username'),
            displayName: getField(subSecs, 'display_name'),
            email:       getField(subSecs, 'email_address'),
            emailStatus: getField(subSecs, 'email_status'),
            phone:       getField(subSecs, 'phone_number'),
            phoneStatus: getField(subSecs, 'phone_status'),
            status:      getField(subSecs, 'status'),
            created:     getField(subSecs, 'created'),
            creationIp:  getField(subSecs, 'creation_ip'),
            lastActive:  getField(subSecs, 'last_active'),
            twoFA:       getField(subSecs, '2FA_status'),
            ghostMode:   getField(locSecs, 'ghost_mode'),
          },
          csvParsed,
        });
      }

      parsed.sort((a, b) => (a.info.username || a.folderName).localeCompare(b.info.username || b.folderName));
      setAccounts(parsed);
      setSelectedIdx(0);
      setAnalysisView(null);
      setPhase('dashboard');
    } catch (e) {
      setError(e.message || 'Failed to process archive.');
      setPhase('upload');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const reset = () => { setPhase('upload'); setAccounts([]); setError(''); };

  // ── UPLOAD / PARSING ──────────────────────────────────────────────────────────
  if (phase !== 'dashboard') {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: FONT }}>
        <Navbar pageTitle="Snapchat Subpoena Processor" />
        <div style={{ maxWidth: 580, margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px', margin: 0 }}>
              Snapchat Subpoena Processor
            </h2>
            <p style={{ color: '#64748b', marginTop: 8, fontSize: 15, lineHeight: 1.6, marginBottom: 0 }}>
              Upload a Snapchat law enforcement return ZIP to browse account details, IP history,
              and privacy settings for each subpoenaed account.
            </p>
          </div>
          <ReviewCaveat what="the actual returns from Snap (Snapchat)" style={{ marginBottom: 20 }} />

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#b91c1c', fontSize: 14 }}>
              {error}
            </div>
          )}

          {phase === 'parsing' ? (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '40px 32px', textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>⚙️</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 8 }}>Processing archive…</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{statusMsg}</div>
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              style={{ border: '2px dashed #cbd5e1', borderRadius: 12, padding: '56px 32px', textAlign: 'center', cursor: 'pointer', background: '#fff' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#fff'; }}
            >
              <div style={{ fontSize: 40, marginBottom: 14 }}>📦</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 6 }}>Drop Snapchat return ZIP here</div>
              <div style={{ fontSize: 13, color: '#94a3b8' }}>or click to browse</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) processFile(e.target.files[0]); }}
              />
            </div>
          )}

          <div style={{ marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, color: '#15803d' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Files are processed entirely in your browser — nothing is ever uploaded to any server.
          </div>

          {/* Expected structure */}
          <div style={{ marginTop: 20, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#94a3b8', marginBottom: 10 }}>Expected ZIP Structure</div>
            <pre style={{ margin: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#475569', lineHeight: 1.7, background: 'none', whiteSpace: 'pre' }}>{
`Archive.zip
├── account-folder-1/
│   ├── subscriber_info.csv
│   ├── ip_data.csv
│   ├── public_profile.csv
│   └── loc_priv_sets.csv
└── account-folder-2/
    ├── subscriber_info.csv
    └── ...`
            }</pre>
          </div>
        </div>
      </div>
    );
  }

  // ── DASHBOARD ─────────────────────────────────────────────────────────────────
  const { info, csvParsed } = account;
  const subSecs = csvParsed.subscriber_info?.sections ?? [];
  const locSecs = csvParsed.loc_priv_sets?.sections ?? [];
  const pubParsed = csvParsed.public_profile;

  // IP summary stats
  const uniqueIpCount = new Set(ipRows.map(r => r.ip)).size;
  const topIp = (() => {
    if (!ipRows.length) return null;
    const counts = {};
    ipRows.forEach(r => { if (r.ip) counts[r.ip] = (counts[r.ip] || 0) + 1; });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return top || null;
  })();

  // Chip helpers
  const statusChip = (val) => {
    if (!val) return null;
    const v = val.toLowerCase();
    if (v === 'active') return <Chip label="Active" color="#15803d" bg="#dcfce7" />;
    if (v === 'disabled' || v === 'deactivated') return <Chip label={val} color="#b91c1c" bg="#fee2e2" />;
    return <Chip label={val} color="#475569" bg="#f1f5f9" />;
  };

  const verifiedChip = (val) => {
    if (!val) return null;
    return val.toUpperCase() === 'TRUE'
      ? <Chip label="Verified" color="#15803d" bg="#dcfce7" />
      : <Chip label="Not Verified" color="#b45309" bg="#fef3c7" />;
  };

  const twoFAChip = (val) => {
    if (!val) return null;
    return val === 'Not Enabled'
      ? <Chip label="No 2FA" color="#b91c1c" bg="#fee2e2" />
      : <Chip label={val} color="#15803d" bg="#dcfce7" />;
  };

  const ghostChip = (val) => {
    if (!val) return null;
    return val === 'true'
      ? <Chip label="Ghost Mode ON" color="#b45309" bg="#fef3c7" />
      : val === 'false'
      ? <Chip label="Ghost Mode OFF" color="#15803d" bg="#dcfce7" />
      : null;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: FONT, display: 'flex', flexDirection: 'column' }}>
      <Navbar pageTitle="Snapchat Subpoena Processor" />

      {/* Top action bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {accounts.length} account{accounts.length !== 1 ? 's' : ''} found
        </span>
        <button
          onClick={reset}
          style={{ background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          New File
        </button>
      </div>

      {/* Body: sidebar + main */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', height: 'calc(100vh - 104px)' }}>

        {/* Sidebar */}
        <div style={{ width: 260, flexShrink: 0, background: '#fff', borderRight: '1px solid #e2e8f0', overflowY: 'auto', padding: '8px 0' }}>
          {accounts.map((acct, idx) => (
            <div
              key={acct.folderName}
              onClick={() => { setSelectedIdx(idx); setAnalysisView(null); }}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderLeft: `3px solid ${idx === selectedIdx && !analysisView ? '#f59e0b' : 'transparent'}`,
                background: idx === selectedIdx && !analysisView ? '#fffbeb' : 'transparent',
                transition: 'background .12s',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                {acct.info.username || acct.folderName}
              </div>
              {acct.info.displayName && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{acct.info.displayName}</div>
              )}
              {acct.dateRange && (
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>{acct.dateRange}</div>
              )}
            </div>
          ))}
          <div style={{ borderTop: '1px solid #e2e8f0', margin: '8px 0 4px', padding: '10px 16px 4px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>
            Analysis<GeneratedMark />
          </div>
          {[
            { key: 'ips', label: `IP analysis: ${account?.info.username || account?.folderName || ''}` },
            ...(accounts.length > 1 ? [{ key: 'links', label: 'Links between accounts' }] : []),
          ].map(v => (
            <div key={v.key} onClick={() => setAnalysisView(v.key)}
              style={{ padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0f172a', borderLeft: `3px solid ${analysisView === v.key ? '#2563eb' : 'transparent'}`, background: analysisView === v.key ? '#eff6ff' : 'transparent', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {v.label}
            </div>
          ))}
        </div>

        {/* Main panel */}
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', padding: '20px 24px 40px' }}>

          <ReviewCaveat what="the actual returns from Snap (Snapchat)" style={{ marginBottom: 16 }} />

          {analysisView ? <SubpoenaAnalysis view={analysisView} accounts={accounts} account={account} /> : (<>

          {/* Account header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>
              👻
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
                {info.displayName || info.username || account.folderName}
              </h2>
              {info.username && info.displayName && (
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>@{info.username}</div>
              )}
            </div>
          </div>

          {/* Account Overview */}
          <SectionCard title="Account Overview">
            <KVRow label="Username" value={info.username} />
            <KVRow label="Display Name" value={info.displayName} />
            <KVRow label="Status" value={null} chip={statusChip(info.status)} />
            <KVRow label="Created" value={info.created} />
            <KVRow label="Last Active" value={info.lastActive} />
            <KVRow label="Creation IP" value={info.creationIp} />
            <KVRow label="Phone" value={info.phone || null} chip={verifiedChip(info.phoneStatus)} />
            <KVRow label="Email" value={info.email || null} chip={verifiedChip(info.emailStatus)} />
            <KVRow label="2FA Status" value={info.twoFA} chip={twoFAChip(info.twoFA)} />
            <KVRow label="Ghost Mode" value={null} chip={ghostChip(info.ghostMode)} />
          </SectionCard>

          {/* Full Subscriber Info */}
          <SectionCard title="Full Subscriber Info">
            {subSecs.length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>No subscriber data found.</div>
            ) : (
              subSecs.map((sec, si) => (
                <div key={si} style={{ marginBottom: si < subSecs.length - 1 ? 20 : 0 }}>
                  {sec.rows.map((row, ri) => (
                    <div key={ri}>
                      {ri > 0 && <div style={{ height: 1, background: '#e2e8f0', margin: '8px 0' }} />}
                      {sec.headers.map(h => (
                        <KVRow key={h} label={h.replace(/_/g, ' ')} value={row[h] || null} />
                      ))}
                    </div>
                  ))}
                  {si < subSecs.length - 1 && <div style={{ height: 1, background: '#cbd5e1', margin: '12px 0 0' }} />}
                </div>
              ))
            )}
          </SectionCard>

          {/* Privacy Settings */}
          <SectionCard title="Privacy Settings">
            {locSecs.length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>No privacy data found.</div>
            ) : (
              locSecs.map((sec, si) => (
                <div key={si}>
                  {sec.rows.map((row, ri) => (
                    sec.headers.map(h => (
                      <div key={h} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ width: 160, fontSize: 12, color: '#64748b', fontWeight: 500, flexShrink: 0 }}>{h.replace(/_/g, ' ')}</div>
                        <div style={{ fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>{row[h]}</span>
                          {h === 'ghost_mode' && ghostChip(row[h])}
                        </div>
                      </div>
                    ))
                  ))}
                </div>
              ))
            )}
          </SectionCard>

          {/* IP Activity */}
          <SectionCard title={`IP Activity${ipRows.length ? ` — ${ipRows.length.toLocaleString()} events` : ''}`}>
            {ipRows.length === 0 ? (
              <div style={{ fontSize: 13, color: '#94a3b8' }}>No IP data found.</div>
            ) : (
              <>
                {/* Summary row */}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14, fontSize: 12, color: '#64748b', paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
                  <span><strong style={{ color: '#0f172a' }}>{ipRows.length.toLocaleString()}</strong> events</span>
                  <span><strong style={{ color: '#0f172a' }}>{uniqueIpCount.toLocaleString()}</strong> unique IPs</span>
                  {topIp && (
                    <span>Most common: <strong style={{ color: '#0f172a', fontFamily: 'monospace' }}>{topIp[0]}</strong> ({topIp[1].toLocaleString()}×)</span>
                  )}
                </div>

                {/* Filter */}
                <input
                  type="text"
                  placeholder="Filter by IP, source, user agent…"
                  value={ipFilter}
                  onChange={e => { setIpFilter(e.target.value); setIpPage(0); }}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', fontSize: 13, borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 12, outline: 'none', fontFamily: FONT }}
                />

                {/* Table — fixed layout so columns don't push past container width */}
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 12 }}>
                  <colgroup>
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '24%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '33%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {['Timestamp (UTC)', 'IP Address', 'ASN', 'Source', 'User Agent'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedIpRows.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: '#475569', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.timestamp}</td>
                        <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: '#0f172a', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.ip}</td>
                        <td style={{ padding: '7px 10px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.asn}>{row.asn}</td>
                        <td style={{ padding: '7px 10px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.source}</td>
                        <td style={{ padding: '7px 10px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.user_agent}>{row.user_agent}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalIpPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 12, color: '#64748b' }}>
                    <span>Page {ipPage + 1} of {totalIpPages} · {filteredIpRows.length.toLocaleString()} rows</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setIpPage(0)} disabled={ipPage === 0} style={{ ...paginBtnStyle, opacity: ipPage === 0 ? 0.4 : 1, cursor: ipPage === 0 ? 'default' : 'pointer' }}>«</button>
                      <button onClick={() => setIpPage(p => Math.max(0, p - 1))} disabled={ipPage === 0} style={{ ...paginBtnStyle, opacity: ipPage === 0 ? 0.4 : 1, cursor: ipPage === 0 ? 'default' : 'pointer' }}>‹</button>
                      <button onClick={() => setIpPage(p => Math.min(totalIpPages - 1, p + 1))} disabled={ipPage >= totalIpPages - 1} style={{ ...paginBtnStyle, opacity: ipPage >= totalIpPages - 1 ? 0.4 : 1, cursor: ipPage >= totalIpPages - 1 ? 'default' : 'pointer' }}>›</button>
                      <button onClick={() => setIpPage(totalIpPages - 1)} disabled={ipPage >= totalIpPages - 1} style={{ ...paginBtnStyle, opacity: ipPage >= totalIpPages - 1 ? 0.4 : 1, cursor: ipPage >= totalIpPages - 1 ? 'default' : 'pointer' }}>»</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </SectionCard>

          {/* Public Profile */}
          <SectionCard title="Public Profile">
            {(pubParsed?.noData || !pubParsed?.sections?.length) ? (
              <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>NO RESPONSIVE DATA FOUND</div>
            ) : (
              pubParsed.sections.map((sec, si) => (
                <div key={si} style={{ marginBottom: si < pubParsed.sections.length - 1 ? 16 : 0 }}>
                  <DataTable headers={sec.headers} rows={sec.rows} />
                </div>
              ))
            )}
          </SectionCard>
          </>)}

        </div>
      </div>
    </div>
  );
}
